import { da, enGB } from 'date-fns/locale';
import type { PortalAppointmentItem } from '@/hooks/usePortalAppointments';
import type { PortalMessage } from '@/hooks/usePortalMessages';
import type { PortalPunchCardItem } from '@/hooks/usePortalPunchCards';
import type { PatientFormsAssignment } from '@/hooks/usePatientFormsAssignments';
import { isPortalUpcomingAppointment } from '@/utils/appointmentEditability';
import { CLINIC_TIMEZONE } from '@/lib/reporting/timezone';

export { greetingPeriod } from '@/utils/greeting';

export function dateFnsLocale(lang: string) {
  return lang.toLowerCase().startsWith('en') ? enGB : da;
}

export function patientInitials(firstName?: string | null, lastName?: string | null): string {
  const first = firstName?.trim().charAt(0) ?? '';
  const last = lastName?.trim().charAt(0) ?? '';
  const initials = `${first}${last}`.toUpperCase();
  return initials || '?';
}

export function nextUpcomingAppointment(
  appointments: PortalAppointmentItem[],
  now = new Date(),
): PortalAppointmentItem | null {
  const upcoming = appointments
    .filter((apt) =>
      isPortalUpcomingAppointment({
        displayStatus: apt.display_status,
        startsAt: apt.starts_at,
        endsAt: apt.ends_at,
        now,
      }),
    )
    .sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime());
  return upcoming[0] ?? null;
}

function clinicLocale(lang: string): string {
  return lang.toLowerCase().startsWith('en') ? 'en-GB' : 'da-DK';
}

export function formatAppointmentDate(iso: string, lang: string): string {
  return new Date(iso).toLocaleDateString(clinicLocale(lang), {
    timeZone: CLINIC_TIMEZONE,
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function formatAppointmentTime(iso: string, lang: string): string {
  return new Date(iso).toLocaleTimeString(clinicLocale(lang), {
    timeZone: CLINIC_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** Civil YYYY-MM-DD in the clinic timezone (not the browser timezone). */
export function clinicIsoDate(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: CLINIC_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

export function isUnreadClinicMessage(message: PortalMessage): boolean {
  return message.sender === 'clinic' && !message.read_at;
}

export function aggregateActivePunchCards(cards: PortalPunchCardItem[]): {
  count: number;
  remaining: number;
  total: number;
} {
  const active = cards.filter((card) => card.status === 'active');
  const total = active.reduce((sum, card) => sum + card.total_clips, 0);
  const used = active.reduce((sum, card) => sum + card.used_clips, 0);
  return {
    count: active.length,
    remaining: Math.max(0, total - used),
    total,
  };
}

export function isPendingQuestionnaire(assignment: PatientFormsAssignment): boolean {
  return assignment.status === 'sent' || assignment.status === 'reminded';
}
